import { useRef, useState, createContext } from 'react';

import { overlayCloseReasons } from './overlayCloseReasons';

export const GlobalOverlayContext = createContext(null);
export const GlobalOverlayActiveContext = createContext(null);

class GlobalOverlay {
  constructor({ setActiveContextState }) {
    this.setActiveContextState = setActiveContextState;
    // Open blocking stacks in the order they became active. The last one is the active stack.
    this.contexts = [];
  }

  getActiveContext() {
    return this.contexts.at(-1) ?? null;
  }

  setActiveContext(context) {
    const previousContext = this.getActiveContext();

    this.contexts = this.contexts.filter((item) => item !== context);
    this.contexts.push(context);
    this.setActiveContextState(context);

    if (previousContext != null && previousContext !== context) {
      closeOnOverlayOpen(previousContext);
    }
  }

  /**
   * Removes a stack whose root unmounted. When it was the active stack, the previous open stack
   * becomes active again, so an overlay left open behind it (e.g. a popover behind a prompt) still
   * closes on an outside press.
   */
  removeContext(context) {
    const previousContext = this.getActiveContext();

    this.contexts = this.contexts.filter((item) => item !== context);

    const nextContext = this.getActiveContext();

    if (nextContext !== previousContext) {
      this.setActiveContextState(nextContext);
    }
  }
}

/**
 * Closes the opted-in overlays of a stack that is no longer active, from the top down. Overlays
 * that did not opt in stay open, unless they are nested in a closing one (e.g. a submenu) and
 * unmount with it.
 */
function closeOnOverlayOpen(context) {
  const overlaysToClose = [];

  for (let index = context.stack.length - 1; index >= 0; index--) {
    const item = context.stack[index];

    if (item.getShouldCloseOnOverlayOpen() !== true) {
      continue;
    }

    if (item.getIsPreventClose() !== true) {
      overlaysToClose.push(item);
    }
  }

  for (const { onCloseRequest } of overlaysToClose) {
    onCloseRequest(overlayCloseReasons.OVERLAY_OPEN);
  }
}

export function GlobalOverlayContextProvider(props) {
  const context = useRef(null);
  const [activeContextState, setActiveContextState] = useState(null);

  if (context.current == null) {
    context.current = new GlobalOverlay({ setActiveContextState });
  }

  return (
    <GlobalOverlayContext.Provider value={context.current}>
      <GlobalOverlayActiveContext.Provider value={activeContextState}>
        {props.children}
      </GlobalOverlayActiveContext.Provider>
    </GlobalOverlayContext.Provider>
  );
}
